import * as React from 'react'
import { DismissButton, FocusScope, mergeProps, useDialog, useModal, useOverlay } from 'react-aria'

interface PopoverProperties {
  title: JSX.Element
  isOpen: boolean
  onClose: () => void
  children: JSX.Element | string
  className?: string
  style?: React.CSSProperties
}

const MultiSelectFilterPopover = React.forwardRef<HTMLDivElement, PopoverProperties>(
  ({ title, children, isOpen, className, style, onClose, ...otherProperties }, reference) => {
    // Handle interacting outside the dialog and pressing
    // the Escape key to close the modal.
    const { overlayProps } = useOverlay(
      {
        onClose,
        isOpen,
        isDismissable: true,
      },
      reference as React.RefObject<HTMLElement>,
    )

    // Hide content outside the modal from screen readers.
    const { modalProps } = useModal()

    // Get props for the dialog and its title
    const { dialogProps, titleProps } = useDialog({}, reference as React.RefObject<HTMLElement>)

    function handleBlur(event: React.FocusEvent<HTMLDivElement>) {
      // relatedTarget is only reliably a concrete element when focus moves to another
      // focusable element (e.g. via Tab). It can be null for non-focus-changing mouse
      // interactions inside the popover (e.g. clicking a non-focusable option), so we
      // only close when we can positively confirm focus landed outside the popover.
      const nextFocusTarget = event.relatedTarget as Node | null
      if (nextFocusTarget && !event.currentTarget.contains(nextFocusTarget)) {
        onClose()
      }
    }

    return (
      <FocusScope restoreFocus>
        <div
          {...mergeProps(overlayProps, dialogProps, otherProperties, modalProps)}
          ref={reference}
          className={className}
          style={{
            ...style,
          }}
          onBlur={handleBlur}
        >
          <div className="cmp-multi-select-filter-popover__header">
            <div
              className="cmp-multi-select-filter-popover__dismiss"
              tabIndex={-1}
              aria-hidden="true"
              onClick={onClose}
            >
              <i className="ri-close-line" />
            </div>
            <div className="cmp-multi-select-filter-popover__title" {...titleProps}>
              {title}
            </div>
          </div>
          {children}
          <DismissButton onDismiss={onClose} />
        </div>
      </FocusScope>
    )
  },
)

export default MultiSelectFilterPopover
